import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { CategoryEditor } from "@ui/components/category/categoryEditor";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { EyeIcon } from "@ui/components/icons";
import { TagsInput } from "@ui/components/inputs/tagsInput/tagsInput";
import { stack } from "@ui/components/layout";
import { ContentShareroomEditor } from "@ui/components/lms/contentShareroomEditor";
import { PublisherSelector } from "@ui/components/lms/publisherSelector";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { useEditorController } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/editorControllerContext";
import { BannerEditor } from "./bannerEditor";
import { DurationEditor } from "./durationEditor";
import { IntroductionEditor } from "./introductionEditor";
import { LanguageEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/languageEditor";
// import { RadioButton } from "../../../../components/inputs/radio";

interface ArticleDetailsEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	articleId?: string;
	loading: boolean;
	onSaveArticle: (track?: "track", toast?: "toast", redirect?: "article" | "editor") => void;
}
export const ArticleDetailsEditor: React.FC<ArticleDetailsEditorProps> = ({
	articleId,
	loading,
	onSaveArticle,
	...htmlProps
}) => {
	const { articleService } = useServices();
	const controller = useEditorController();
	const article = useObservable(controller.article);
	const askDeleteConfirmation = useConfirmation(
		I18n.t("article.delete.title"),
		I18n.t("delete"),
		I18n.t("article.delete.message")
	);
	const history = useHistory();

	return (
		<Container {...htmlProps}>
			{/*<StyledArticleStatusLabel status={article.status} />*/}
			<DetailsBlock>
				<PrimaryBold>{I18n.t("article.editor.details.settings")}</PrimaryBold>
				<CategoryEditor
					category={article.category}
					subCategory={article.subCategory}
					setCategory={(c) => controller.setCategory(c)}
					setSubCategory={(c) => controller.setSubCategory(c)}
					label={I18n.t("category.placeholder.article")}
				/>
				<DurationEditor />
				<LanguageEditor />
				<TagsInput
					tags={article.tags}
					setTags={(newTags: string[]) => {
						article.tags = newTags;
					}}
					forbidDupplicates
				/>
			</DetailsBlock>
			<PresentationBlock>
				<PrimaryBold>{I18n.t("article.editor.details.presentation")}</PrimaryBold>
				<BannerEditor />
				<IntroductionEditor />
				{/*<DescriptionEditor*/}
				{/*	placeholder={I18n.t("article.editor.description.placeholder") + "*"}*/}
				{/*	value={article.description}*/}
				{/*	onChange={(value) => controller.setDescription(value)}*/}
				{/*/>*/}
			</PresentationBlock>
			<ContentShareroomEditor
				draft={article}
				onToggleWorkspace={(id) => controller.toggleWorkspace(id)}
				onTogglePrivacy={(toggled) => controller.setVisibility(toggled ? "PRIVATE" : "PUBLIC")}
			/>
			<PublicationBlock>
				{/* <PrimaryBold>Mettre ala une</PrimaryBold> */}
				<PrimaryBold>{I18n.t("article.editor.details.publication")}</PrimaryBold>
				{/* <RadioButton
					label={I18n.t("article.editor.details.news.label")}
					name="news"
					id="news"
					disabled={!controller.isValidForDraft()}
					checked={article.articleType === "NEWS"}
					checkbox
					onChange={() => {
						if (article.articleType !== "NEWS") {
							controller.setArticleType("NEWS");
						} else {
							controller.setArticleType(undefined);
						}
					}}
				/> */}
				{article.status !== "PUBLISHED" && (
					<LoadingButton
						buttonType="secondary"
						loading={loading}
						disabled={!controller.isValidForDraft()}
						onClick={() => onSaveArticle("track", "toast", "editor")}
					>
						{I18n.t("article.editor.save.draft")}
					</LoadingButton>
				)}
				{article.status !== "PUBLISHED" && (
					<LoadingButton
						buttonType="secondary"
						loading={loading}
						disabled={!controller.isValidForDraft()}
						onClick={() => onSaveArticle("track", "toast", "article")}
					>
						<EyeIcon color={controller.isValidForDraft() ? theme.colors.red : theme.colors.white} />
						{I18n.t("article.editor.save.preview")}
					</LoadingButton>
				)}
				<PublisherSelector
					initialContent={article}
					onPublisherChange={(publisher) => {
						controller.setPublisher(publisher);
					}}
					onKeepOwnershipChange={(keepOwnership) => {
						controller.setOwnership(keepOwnership);
					}}
				/>
				<LoadingButton
					loading={loading}
					disabled={!controller.isValidForPublication()}
					onClick={
						article.status === "PUBLISHED"
							? () => onSaveArticle("track", "toast", "article")
							: () => controller.setStatus("PUBLISHED")
					}
				>
					{I18n.t("article.editor.publish")}
				</LoadingButton>
			</PublicationBlock>
			<TertiaryActions>
				{article.status === "PUBLISHED" && (
					<TertiaryButton onClick={() => controller.setStatus("ARCHIVED")}>
						{I18n.t("article.editor.archive")}
					</TertiaryButton>
				)}
				{!!articleId && (
					<TertiaryButton
						onClick={() =>
							askDeleteConfirmation(async () => {
								await articleService.deleteArticle(articleId);
								history.push(Path.Root);
								createToast(I18n.t("article.toast.delete.title"), I18n.t("article.toast.delete.message"), "success");
							})
						}
					>
						{I18n.t("article.editor.delete")}
					</TertiaryButton>
				)}
			</TertiaryActions>
		</Container>
	);
};

const Container = styled.div`
	${stack(20, "flex-start", "stretch")};
`;

const PublicationBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const DetailsBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const PresentationBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const TertiaryActions = styled.div`
	align-self: center;
	@media ${mobile} {
		margin-top: 20px;
	}
	${gap(10)};

	> button {
		margin: 0 auto;
		text-decoration: none;
		${({ theme }) => theme.textStyles.label};
		font-size: 0.9375rem;
	}
`;
